import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Clock, Phone, Instagram, Send } from "lucide-react";
import { SplitReveal, FadeUp } from "../components/SplitReveal";
import { useLanguage, CONTACT, waLink } from "../lib/i18n";
import { api } from "../lib/api";

export function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", phone: "", vehicle: "", service: t.contact.serviceOptions[0], note: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t.contact.required);
      return;
    }
    setLoading(true);
    try {
      await api.submitContact(form);
      toast.success(t.contact.success);
      const msg = `Merhaba HARSPRO, randevu talebim:\n👤 ${form.name}\n📞 ${form.phone}\n🚗 ${form.vehicle}\n🛠️ ${form.service}\n📝 ${form.note}`;
      setTimeout(() => window.open(waLink(msg), "_blank"), 700);
      setForm({ name: "", phone: "", vehicle: "", service: t.contact.serviceOptions[0], note: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#08090B] px-4 py-3 font-inter text-sm text-white placeholder:text-hars-muted focus:border-hars-orange focus:outline-none";

  return (
    <section id="contact" className="relative bg-[#08090B] px-6 py-24 md:px-12 md:py-40" data-testid="contact-section">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-14 max-w-2xl">
          <FadeUp>
            <p className="heading mb-4 text-sm not-italic tracking-[0.3em] text-hars-orange">{t.contact.kicker}</p>
          </FadeUp>
          <SplitReveal text={t.contact.title} className="heading text-5xl text-white md:text-7xl" />
        </div>

        <div className="grid gap-10 lg:grid-cols-2">
          {/* Form */}
          <div className="rounded-3xl border border-white/10 bg-[#0F1115] p-8">
            <h3 className="heading mb-6 text-2xl text-white">{t.contact.formTitle}</h3>
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <input className={inputCls} placeholder={t.contact.name} value={form.name} onChange={set("name")} data-testid="contact-name" aria-label={t.contact.name} />
              <input className={inputCls} placeholder={t.contact.phone} value={form.phone} onChange={set("phone")} data-testid="contact-phone" aria-label={t.contact.phone} />
              <input className={inputCls} placeholder={t.contact.vehicle} value={form.vehicle} onChange={set("vehicle")} data-testid="contact-vehicle" aria-label={t.contact.vehicle} />
              <select className={inputCls} value={form.service} onChange={set("service")} data-testid="contact-service" aria-label={t.contact.service}>
                {t.contact.serviceOptions.map((o) => (
                  <option key={o} value={o} className="bg-[#0F1115]">{o}</option>
                ))}
              </select>
              <textarea className={`${inputCls} min-h-[100px] resize-none`} placeholder={t.contact.note} value={form.note} onChange={set("note")} data-testid="contact-note" aria-label={t.contact.note} />
              <button
                type="submit"
                disabled={loading}
                data-cursor
                data-cursor-label="GÖNDER"
                data-testid="contact-submit"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-hars-orange px-6 py-3.5 font-inter text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
              >
                <Send size={16} /> {loading ? t.contact.sending : t.contact.submit}
              </button>
            </form>
          </div>

          {/* Map + info */}
          <div className="flex flex-col gap-6">
            <div className="relative h-64 overflow-hidden rounded-3xl border border-white/10 grayscale-[0.3]">
              {/* Google Maps embed — no API key required */}
              <iframe
                title="HARSPRO Konum"
                src={CONTACT.mapEmbed}
                className="h-full w-full"
                style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) contrast(0.9)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
            <div className="grid gap-4">
              <InfoRow icon={<MapPin size={18} />} title={t.contact.addressTitle} value={CONTACT.address} />
              <InfoRow icon={<Clock size={18} />} title={t.contact.hoursTitle} value={t.contact.hours} />
              <InfoRow icon={<Phone size={18} />} title={t.contact.phoneTitle} value={CONTACT.phoneDisplay} />
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-cursor
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0F1115] p-4 transition-colors hover:border-hars-orange"
              >
                <span className="text-hars-orange"><Instagram size={18} /></span>
                <span className="font-inter text-sm text-white">{CONTACT.instagramHandle}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon, title, value }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-[#0F1115] p-4">
      <span className="mt-0.5 text-hars-orange">{icon}</span>
      <div>
        <p className="font-inter text-xs uppercase tracking-widest text-hars-muted">{title}</p>
        <p className="mt-1 font-inter text-sm text-white">{value}</p>
      </div>
    </div>
  );
}
